import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useCallback, useEffect, useState } from 'react';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';

// Client data layer for the home's "Since you last checked in" pile-up — the jobs an agent finished
// (or still has in motion) while the user was away. Sourced from REAL data: `/api/piled-up-jobs` (the
// AgentTask completed/in-progress query + agent/workspace enrichment). agent_task is the go-forward
// task entity, so metasites not yet on the AgentTask rollout return an empty list and the section
// renders nothing. Refreshes via the AgentTask duplexer as the agents' world changes.


/** One job in the pile-up. Shared shape with the native ports (`PiledUpJob` on iOS/Android). */
export interface PiledUpJob {
  id: string;
  title: string;
  /** The handling agent's id — the per-task icon (agent dot) is keyed on this, matching Your Agents. */
  agentId: string;
  agentName: string;
  /** The owning workspace's id/name/icon — an unassigned task (no agent) falls back to the workspace
   *  badge (its `workspaceIcon`, else initials). `workspaceIcon` is "" when the workspace has no image. */
  workspaceId: string;
  workspaceName: string;
  workspaceIcon: string;
  status: 'completed' | 'inProgress';
  /** Epoch ms. */
  completedAt: number;
}

export async function fetchPiledUpJobs(): Promise<PiledUpJob[]> {
  const res = await apiFetch(`${API_BASE}/api/piled-up-jobs`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { jobs?: PiledUpJob[] };
  return data.jobs ?? [];
}

// Module cache so returning home shows the pile instantly; the AgentTask duplexer refreshes it as
// the agents' world changes. `null` = not loaded (or unavailable) — the section renders nothing;
// `[]` = loaded-but-empty, also nothing.
let cache: PiledUpJob[] | null = null;

// The section's data source. Resolved in an effect so SSR and the first client render agree (both
// null); the real batch appears post-mount, avoiding a hydration mismatch.
export function usePiledUpJobs(): PiledUpJob[] | null {
  const [jobs, setJobs] = useState<PiledUpJob[] | null>(cache);

  const load = useCallback(() => {
    let cancelled = false;
    fetchPiledUpJobs()
      .then((next) => {
        cache = next;
        if (!cancelled) setJobs(next);
      })
      .catch(() => {
        /* unavailable — keep whatever we had; the section degrades to absent */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);
  useAgentTaskDuplexer(() => load());

  return jobs;
}

/** The completed jobs (the expanded "completed" rows; the deck now stacks all tasks). */
export function completedJobs(jobs: PiledUpJob[]): PiledUpJob[] {
  return jobs.filter((job) => job.status === 'completed');
}

/** The in-progress jobs (the expanded "IN PROGRESS" subsection). */
export function inProgressJobs(jobs: PiledUpJob[]): PiledUpJob[] {
  return jobs.filter((job) => job.status === 'inProgress');
}

// Java `String.hashCode`-compatible hash (matches `colorForAgent`'s), so the per-batch id is stable across
// process restarts.
function javaHash(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// A stable id for this batch of piled-up jobs (completed + in-progress) — the play-once "seen" marker keys
// on it, so the same batch never re-cascades and a genuinely new batch plays once. Per-device only; not
// compared across platforms.
export function piledUpBatchId(jobs: PiledUpJob[]): string {
  const ids = jobs.map((job) => job.id).join(',');
  return `piled-${javaHash(ids)}`;
}

// The header renders a bold count lead + a regular label ("12" · "Jobs piled up while you slept."). Given
// the already-localized full sentence (built from the i18n catalog at the call site), split off a leading
// count token so ONLY the number is emphasised; the first letter of the remainder is capitalised so it
// reads as the sentence lead. A locale whose sentence does NOT begin with the count keeps `countLead` empty
// and stays fully regular, so no word order is broken. Pure; mirrors the native `PiledUp.headerParts` rule.
export function piledUpHeaderParts(count: number, sentence: string): { countLead: string; label: string } {
  const lead = `${count} `;
  if (sentence.startsWith(lead)) {
    const rest = sentence.slice(lead.length);
    const label = rest ? rest[0].toUpperCase() + rest.slice(1) : rest;
    return { countLead: `${count}`, label };
  }
  return { countLead: '', label: sentence };
}

// --- Collapsed deck navigation (the mobile "piled up" swipe deck) -------------------------------------
//
// The collapsed deck is a ROTATING horizontal stack, held as an `order` array of job indices: the front
// card is `order[0]`, the back card is `order[order.length - 1]`. Swiping the front card in EITHER
// direction sends it to the BACK (`cycleForward`) and brings the next card up, so the deck never empties
// to a single card; the fling direction only picks which way it flies off, the resulting order is the same.
// A monotonic `viewed` set records which indices have ever been the swiped-away front, for the tri-state
// pagination dots. All pure + testable, shared by the deck rendering (web desktop keeps the strip).

/** The collapsed stack shows the front card plus at most this many peeking behind it. */
export const PILE_MAX_BEHIND = 2;

/** A freshly revealed deck: front = index 0, i.e. `[0, 1, …, deckCount - 1]`. */
export function initialOrder(deckCount: number): number[] {
  return Array.from({ length: Math.max(deckCount, 0) }, (_, i) => i);
}

/** Move the front card to the BACK of the stack (rotate left). An empty deck is returned unchanged. */
export function cycleForward(order: number[]): number[] {
  return order.length === 0 ? order : [...order.slice(1), order[0]];
}

/** The outcome of releasing a horizontal drag. */
export type SwipeResult = 'forward' | 'backward' | 'none';

/** Right past `+threshold` → forward, left past `-threshold` → backward, otherwise none (snap back). */
export function commitSwipe(dx: number, threshold: number): SwipeResult {
  if (dx > threshold) return 'forward';
  if (dx < -threshold) return 'backward';
  return 'none';
}

/** The deck order after a committed swipe: both directions send the front card to the back; none is a no-op. */
export function reorderAfterSwipe(order: number[], result: SwipeResult): number[] {
  return result === 'none' ? order : cycleForward(order);
}

/** A pagination dot's tri-state. */
export type DotState = 'active' | 'viewed' | 'unviewed';

/** The current front card is `active`; a card ever swiped away is `viewed`; one not yet reached is `unviewed`. */
export function dotState(deckIndex: number, order: number[], viewed: ReadonlySet<number>): DotState {
  if (order[0] === deckIndex) return 'active';
  if (viewed.has(deckIndex)) return 'viewed';
  return 'unviewed';
}

/** Resting opacity for a card at order-position `p` (0 = front): the front is solid, the two behind fade
 *  back, anything deeper is hidden — so the collapsed stack reads as a 3-card depth. */
export function restOpacity(p: number): number {
  if (p <= 0) return 1;
  if (p === 1) return 0.9;
  if (p === 2) return 0.6;
  return 0;
}

/** The two upper cards cast a shadow onto the card beneath (depth); the deepest casts none. */
export function castsShadow(p: number): boolean {
  return p <= 1;
}

/** Cards peeking BEHIND the front for a deck of `deckCount`, capped at `maxBehind` — so the deck's deepest
 *  visible card anchors flush at x=0 (aligned with the header) at any card count. */
export function visibleBehind(deckCount: number, maxBehind: number = PILE_MAX_BEHIND): number {
  return Math.max(0, Math.min(deckCount - 1, maxBehind));
}

/** Play the deal-out entrance only when this batch hasn't been seen yet. */
export function shouldPlayDeal(batchId: string, lastSeen: string): boolean {
  return batchId !== lastSeen;
}

// Play-once persistence (web equivalent of the native OneTimeState). Keyed on the same storage id as the
// native ports for consistency; web localStorage is a separate per-device store.
const SEEN_KEY = 'symphony.piledUp.cascadeSeenId';

export function readPiledUpSeen(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(SEEN_KEY) ?? '';
  } catch {
    return '';
  }
}

export function markPiledUpSeen(batchId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SEEN_KEY, batchId);
  } catch {
    /* storage unavailable (private mode / quota) — the cascade simply replays next time */
  }
}
